import React, { useEffect, useMemo, useState } from 'react';
import { Settings, ListTree, Search, Plus, Pencil, Trash2, FileText } from 'lucide-react';
import { Heading } from '../../layout';
import { FormDrawer } from '../../forms';
import { SettingsSplit } from '../../reusable';
import { useUIColors } from '../../../hooks/useTheme';
import useFinanceChartConfig, {
  DETAIL_KEYS,
  EMPTY_CHART_ACCOUNT,
  normalize,
} from '../../../hooks/useFinanceChartConfig';
import FinanceAccountingRulesPanel from './FinanceAccountingRulesPanel';

const NAV_ITEMS = [
  { key: 'general', label: 'General', icon: Settings },
  { key: 'chart-accounts', label: 'Plan de cuentas', icon: ListTree },
  { key: 'accounting-rules', label: 'Reglas contables', icon: FileText },
];

const FinanceSettingsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const chart = useFinanceChartConfig();
  const [section, setSection] = useState('chart-accounts');
  const [showLevelsDrawer, setShowLevelsDrawer] = useState(false);
  const [showNodeDrawer, setShowNodeDrawer] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingChartAccountId, setEditingChartAccountId] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSavingLevels, setIsSavingLevels] = useState(false);
  const [isSavingNode, setIsSavingNode] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [chartAccountFormData, setChartAccountFormData] = useState({ ...EMPTY_CHART_ACCOUNT });
  const [detailNodeDraft, setDetailNodeDraft] = useState({
    level_key: 'detail_level_1',
    parent_node_id: '',
    node_name: '',
  });
  const [detailNodeError, setDetailNodeError] = useState('');

  useEffect(() => {
    if (!chart.hierarchyLevels.length) return;
    const valid = chart.hierarchyLevels.some((row) => row.level_key === detailNodeDraft.level_key);
    if (!valid) {
      setDetailNodeDraft((prev) => ({
        ...prev,
        level_key: chart.hierarchyLevels[0].level_key,
        parent_node_id: '',
      }));
    }
  }, [chart.hierarchyLevels, detailNodeDraft.level_key]);

  const parentOptionsForDraft = useMemo(() => {
    const currentIndex = chart.hierarchyLevels.findIndex(
      (row) => row.level_key === detailNodeDraft.level_key
    );
    if (currentIndex <= 0) return [];
    const parentLevelKey = chart.hierarchyLevels[currentIndex - 1].level_key;
    return chart.detailNodesByLevel.get(parentLevelKey) || [];
  }, [chart.hierarchyLevels, chart.detailNodesByLevel, detailNodeDraft.level_key]);

  const chartAccountFormSections = useMemo(() => {
    const fields = chart.detailLevelsOrdered.map((level, idx) => {
      const key = `${level.level_key}_id`;
      const parentKey = idx > 0 ? `${chart.detailLevelsOrdered[idx - 1].level_key}_id` : null;
      const parentSelectedId = parentKey ? Number(chartAccountFormData[parentKey] || 0) : null;
      const baseOptions = chart.detailNodesByLevel.get(level.level_key) || [];
      const filteredOptions =
        idx === 0
          ? baseOptions.filter((node) => node.parent_node_id === null)
          : baseOptions.filter((node) => Number(node.parent_node_id) === parentSelectedId);
      return {
        key,
        label: level.level_name || level.level_key,
        type: 'select',
        options: [
          { value: '', label: `Selecciona ${level.level_name || level.level_key}` },
          ...filteredOptions.map((node) => ({
            value: String(node.id),
            label: `${node.full_code ? `${node.full_code} - ` : ''}${node.node_name}`,
          })),
        ],
        fullWidth: true,
      };
    });
    return [
      { key: 'details', title: 'Niveles de detalle', fields },
      {
        key: 'notes',
        title: 'Notas',
        fields: [{ key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 4 }],
      },
    ];
  }, [chart.detailLevelsOrdered, chart.detailNodesByLevel, chartAccountFormData]);

  const panelStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: '12px',
    background: ui.panel,
  };

  const inputStyle = {
    height: '34px',
    borderRadius: '8px',
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.inputText,
    padding: '0 10px',
    fontSize: '13px',
    boxSizing: 'border-box',
    width: '100%',
  };

  const primaryBtn = {
    height: '34px',
    borderRadius: '8px',
    border: 'none',
    background: ui.interactive,
    color: ui.textOnAccent || '#ffffff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };

  const ghostBtn = {
    height: '34px',
    borderRadius: '8px',
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };

  const openCreateChartAccount = () => {
    setEditingChartAccountId(null);
    setFormError('');
    setChartAccountFormData({ ...EMPTY_CHART_ACCOUNT });
    setShowAccountForm(true);
  };

  const openEditChartAccount = (row) => {
    if (!row) return;
    setEditingChartAccountId(row.id);
    setFormError('');
    const mappedIds = chart.findNodeIdByPath(row);
    setChartAccountFormData({
      detail_level_1_id: mappedIds.detail_level_1_id || '',
      detail_level_2_id: mappedIds.detail_level_2_id || '',
      detail_level_3_id: mappedIds.detail_level_3_id || '',
      detail_level_4_id: mappedIds.detail_level_4_id || '',
      detail_level_5_id: mappedIds.detail_level_5_id || '',
      detail_level_6_id: mappedIds.detail_level_6_id || '',
      notes: row.notes || '',
    });
    setShowAccountForm(true);
  };

  const handleChartAccountFieldChange = (key, value) => {
    setChartAccountFormData((prev) => {
      if (!key.endsWith('_id')) return { ...prev, [key]: value };
      const next = { ...prev, [key]: value };
      const levelIndex = DETAIL_KEYS.findIndex((levelKey) => `${levelKey}_id` === key);
      if (levelIndex >= 0) {
        for (let idx = levelIndex + 1; idx < DETAIL_KEYS.length; idx += 1) {
          next[`${DETAIL_KEYS[idx]}_id`] = '';
        }
      }
      return next;
    });
  };

  const handleSaveChartAccount = async () => {
    const hasAnyDetail = DETAIL_KEYS.some(
      (key) => String(chartAccountFormData[`${key}_id`] || '').trim().length > 0
    );
    if (!hasAnyDetail) {
      setFormError('Debes completar al menos un nivel de detalle.');
      return;
    }
    try {
      setIsSavingAccount(true);
      setFormError('');
      await chart.saveChartAccount(chartAccountFormData, editingChartAccountId);
      setShowAccountForm(false);
      setEditingChartAccountId(null);
      setChartAccountFormData({ ...EMPTY_CHART_ACCOUNT });
    } catch (err) {
      setFormError(err.message || 'Error guardando registro.');
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleDeleteChartAccount = async (row) => {
    if (!window.confirm('¿Eliminar esta cuenta contable?')) return;
    try {
      await chart.removeChartAccounts(row);
    } catch (err) {
      window.alert(err.message || 'Error eliminando cuenta.');
    }
  };

  const handleSaveLevels = async () => {
    try {
      setIsSavingLevels(true);
      await chart.saveLevels();
      setShowLevelsDrawer(false);
    } catch (err) {
      window.alert(err.message || 'Error guardando niveles.');
    } finally {
      setIsSavingLevels(false);
    }
  };

  const handleSaveDetailNode = async () => {
    const nodeName = String(detailNodeDraft.node_name || '').trim();
    const currentIndex = chart.hierarchyLevels.findIndex(
      (row) => row.level_key === detailNodeDraft.level_key
    );
    if (!nodeName) {
      setDetailNodeError('Debes ingresar un nombre para el nivel.');
      return;
    }
    if (currentIndex > 0 && !detailNodeDraft.parent_node_id) {
      setDetailNodeError('Debes seleccionar un elemento padre del nivel anterior.');
      return;
    }
    try {
      setIsSavingNode(true);
      setDetailNodeError('');
      await chart.saveDetailNode(detailNodeDraft);
      setDetailNodeDraft((prev) => ({ ...prev, node_name: '' }));
      setShowNodeDrawer(false);
    } catch (err) {
      setDetailNodeError(err.message || 'Error guardando nodo.');
    } finally {
      setIsSavingNode(false);
    }
  };

  const renderTreeNode = (node, depth = 0) => {
    const isActive =
      chart.selectedPath.length === node.path.length &&
      chart.selectedPath.every(
        (segment, index) =>
          segment.levelKey === node.path[index].levelKey &&
          normalize(segment.value) === normalize(node.path[index].value)
      );
    const code = chart.detailNodeById.get(Number(node.nodeId))?.full_code;
    return (
      <div key={`${node.level.level_key}-${node.path.map((item) => item.value).join('|')}`}>
        <button
          type="button"
          onClick={() => chart.setSelectedPath(node.path)}
          style={{
            display: 'block',
            width: `calc(100% - ${depth * 10}px)`,
            textAlign: 'left',
            marginLeft: `${depth * 10}px`,
            borderRadius: '8px',
            border: `1px solid ${isActive ? ui.interactive : ui.border}`,
            padding: '7px 10px',
            background: isActive ? ui.interactive : 'transparent',
            color: isActive ? ui.textOnAccent || '#fff' : ui.text,
            fontSize: '12px',
            cursor: 'pointer',
            marginBottom: '4px',
          }}
        >
          {code ? `${code} · ` : ''}
          {node.label}
          <span style={{ opacity: 0.7 }}> ({node.count})</span>
        </button>
        {(node.children || []).map((child) => renderTreeNode(child, depth + 1))}
      </div>
    );
  };

  const accountDisplayName = (row) => {
    for (let i = DETAIL_KEYS.length - 1; i >= 0; i -= 1) {
      const value = String(row[DETAIL_KEYS[i]] || '').trim();
      if (value) return value;
    }
    return 'Sin nombre';
  };

  const accountCodeHint = (row) => {
    const mapped = chart.findNodeIdByPath(row);
    for (let i = DETAIL_KEYS.length - 1; i >= 0; i -= 1) {
      const id = Number(mapped[`${DETAIL_KEYS[i]}_id`] || 0);
      if (id) {
        const node = chart.detailNodeById.get(id);
        if (node?.full_code) return node.full_code;
      }
    }
    return '—';
  };

  const renderGeneral = () => (
    <div style={{ ...panelStyle, padding: '20px', display: 'grid', gap: '16px', maxWidth: 560 }}>
      <div>
        <p style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: ui.text }}>Parámetros generales</p>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: ui.muted }}>
          Valores base de la compañía. La persistencia de moneda y cierres llegará en una iteración posterior.
        </p>
      </div>
      <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: ui.textSecondary }}>
        Moneda base
        <input value="CLP — Peso chileno" disabled style={{ ...inputStyle, opacity: 0.85 }} />
      </label>
      <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: ui.textSecondary }}>
        País / jurisdicción
        <input value="Chile" disabled style={{ ...inputStyle, opacity: 0.85 }} />
      </label>
      <p style={{ margin: 0, fontSize: '12px', color: ui.muted }}>
        Próximos parámetros: inicio de ejercicio fiscal, cierres contables y validaciones por módulo.
      </p>
    </div>
  );

  const renderChartAccounts = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 340px) minmax(0, 1fr)',
        gap: '12px',
        alignItems: 'start',
      }}
    >
      <div style={{ ...panelStyle, padding: '14px', display: 'grid', gap: '10px', position: 'sticky', top: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: ui.text }}>Jerarquía</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={ghostBtn} onClick={() => setShowLevelsDrawer(true)}>
              Niveles
            </button>
            <button
              type="button"
              style={ghostBtn}
              onClick={() => {
                setDetailNodeError('');
                setDetailNodeDraft({
                  level_key: chart.hierarchyLevels[0]?.level_key || 'detail_level_1',
                  parent_node_id: '',
                  node_name: '',
                });
                setShowNodeDrawer(true);
              }}
            >
              <Plus size={14} /> Nodo
            </button>
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <Search
            size={14}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: ui.muted }}
          />
          <input
            value={chart.treeSearch}
            onChange={(e) => chart.setTreeSearch(e.target.value)}
            placeholder="Buscar por código o nombre"
            style={{ ...inputStyle, paddingLeft: 30 }}
          />
        </div>
        <div
          style={{
            border: `1px solid ${ui.border}`,
            borderRadius: 8,
            padding: 8,
            background: ui.section || ui.header,
            maxHeight: '62vh',
            overflowY: 'auto',
          }}
        >
          {!chart.visibleTree.length ? (
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
              No hay nodos aún. Configura niveles y agrega el primer elemento.
            </p>
          ) : (
            chart.visibleTree.map((node) => renderTreeNode(node))
          )}
        </div>
      </div>

      <div style={{ ...panelStyle, padding: '14px', display: 'grid', gap: '12px', minHeight: '68vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>Cuentas contables</p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
              {chart.selectedPathLabel} · {chart.filteredChartAccounts.length} registro(s)
            </p>
          </div>
          <button type="button" style={primaryBtn} onClick={openCreateChartAccount}>
            <Plus size={14} /> Nueva cuenta
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: ui.muted, borderBottom: `1px solid ${ui.border}` }}>
                <th style={{ padding: '8px 6px', fontWeight: 600 }}>Código</th>
                <th style={{ padding: '8px 6px', fontWeight: 600 }}>Nombre</th>
                <th style={{ padding: '8px 6px', fontWeight: 600 }}>Notas</th>
                <th style={{ padding: '8px 6px', fontWeight: 600, width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {!chart.filteredChartAccounts.length ? (
                <tr>
                  <td colSpan={4} style={{ padding: '16px 6px', color: ui.muted }}>
                    No hay cuentas en esta rama.
                  </td>
                </tr>
              ) : (
                chart.filteredChartAccounts.map((row) => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${ui.border}` }}>
                    <td style={{ padding: '10px 6px', color: ui.text, fontVariantNumeric: 'tabular-nums' }}>
                      {accountCodeHint(row)}
                    </td>
                    <td style={{ padding: '10px 6px', color: ui.text, fontWeight: 600 }}>
                      {accountDisplayName(row)}
                    </td>
                    <td style={{ padding: '10px 6px', color: ui.muted }}>{row.notes || '—'}</td>
                    <td style={{ padding: '10px 6px' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          title="Editar"
                          onClick={() => openEditChartAccount(row)}
                          style={{ ...ghostBtn, height: 28, padding: '0 8px' }}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          title="Eliminar"
                          onClick={() => handleDeleteChartAccount(row)}
                          style={{
                            ...ghostBtn,
                            height: 28,
                            padding: '0 8px',
                            borderColor: ui.danger,
                            color: ui.danger,
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>
        Ajustes de Finanzas
      </Heading>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: ui.muted }}>
        Configuración del plan de cuentas y parámetros base del módulo financiero.
      </p>

      {chart.error ? (
        <p style={{ fontSize: 13, color: ui.danger, marginBottom: 12 }}>{chart.error}</p>
      ) : null}

      <SettingsSplit
        ariaLabel="Ajustes de finanzas"
        items={NAV_ITEMS}
        activeKey={section}
        onChange={setSection}
      >
        {chart.isLoading && section !== 'accounting-rules' ? (
          <p style={{ fontSize: 13, color: ui.muted }}>Cargando configuración...</p>
        ) : section === 'general' ? (
          renderGeneral()
        ) : section === 'accounting-rules' ? (
          <FinanceAccountingRulesPanel
            ui={ui}
            chartAccounts={chart.chartAccounts}
            isDarkMode={isDarkMode}
          />
        ) : (
          renderChartAccounts()
        )}
      </SettingsSplit>

      <FormDrawer
        isOpen={showAccountForm}
        onClose={() => setShowAccountForm(false)}
        onSave={handleSaveChartAccount}
        isSaving={isSavingAccount}
        isDarkMode={isDarkMode}
        title={editingChartAccountId ? 'Editar cuenta contable' : 'Nueva cuenta contable'}
        subtitle="Selecciona la ruta de niveles del plan de cuentas"
        sections={chartAccountFormSections}
        formData={chartAccountFormData}
        onChange={handleChartAccountFieldChange}
        formError={formError}
        width={620}
      />

      {showLevelsDrawer ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowLevelsDrawer(false)}
        >
          <div
            style={{
              width: 420,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${ui.border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Configurar niveles</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                Nombre y activación de cada nivel del plan de cuentas.
              </p>
            </div>
            <div style={{ display: 'grid', gap: 10, overflowY: 'auto' }}>
              {chart.detailLevelsOrdered.map((level) => (
                <div
                  key={level.level_key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '64px 1fr 90px',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: 12, color: ui.muted }}>N{level.level_order}</span>
                  <input
                    value={level.level_name || ''}
                    onChange={(e) => chart.updateLevelDraft(level.level_key, 'level_name', e.target.value)}
                    style={inputStyle}
                  />
                  <select
                    value={String(level.is_enabled) === '1' || level.is_enabled === true ? '1' : '0'}
                    onChange={(e) =>
                      chart.updateLevelDraft(level.level_key, 'is_enabled', Number(e.target.value))
                    }
                    style={inputStyle}
                  >
                    <option value="0">Off</option>
                    <option value="1">On</option>
                  </select>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={ghostBtn} onClick={() => setShowLevelsDrawer(false)}>
                Cancelar
              </button>
              <button type="button" style={primaryBtn} onClick={handleSaveLevels} disabled={isSavingLevels}>
                {isSavingLevels ? 'Guardando...' : 'Guardar niveles'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showNodeDrawer ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowNodeDrawer(false)}
        >
          <div
            style={{
              width: 420,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${ui.border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Agregar nodo</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                Nuevo elemento en la jerarquía del plan de cuentas.
              </p>
            </div>
            <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Nivel
                <select
                  value={detailNodeDraft.level_key}
                  onChange={(e) =>
                    setDetailNodeDraft({
                      level_key: e.target.value,
                      parent_node_id: '',
                      node_name: '',
                    })
                  }
                  style={inputStyle}
                >
                  {chart.hierarchyLevels.map((level) => (
                    <option key={level.level_key} value={level.level_key}>
                      {level.level_name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Padre
                <select
                  value={detailNodeDraft.parent_node_id}
                  onChange={(e) =>
                    setDetailNodeDraft((prev) => ({ ...prev, parent_node_id: e.target.value }))
                  }
                  disabled={!parentOptionsForDraft.length}
                  style={inputStyle}
                >
                  <option value="">
                    {parentOptionsForDraft.length ? 'Selecciona padre' : 'Nivel raíz'}
                  </option>
                  {parentOptionsForDraft.map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.full_code ? `${node.full_code} - ` : ''}
                      {node.node_name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Nombre
                <input
                  value={detailNodeDraft.node_name}
                  onChange={(e) =>
                    setDetailNodeDraft((prev) => ({ ...prev, node_name: e.target.value }))
                  }
                  placeholder="Nombre del elemento"
                  style={inputStyle}
                />
              </label>
              {detailNodeError ? (
                <p style={{ margin: 0, fontSize: 12, color: ui.danger }}>{detailNodeError}</p>
              ) : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={ghostBtn} onClick={() => setShowNodeDrawer(false)}>
                Cancelar
              </button>
              <button type="button" style={primaryBtn} onClick={handleSaveDetailNode} disabled={isSavingNode}>
                {isSavingNode ? 'Guardando...' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FinanceSettingsWorkspace;
